import { ChevronLeft, ChevronRight, ExternalLink, PlayCircle, RotateCcw, X } from 'lucide-react';
import {
  resolveAlertHeroStyle,
  hasExtraDetails,
  hasRichTextContent,
  safeImageUrl,
} from '@/lib/alertHeroStyle';
import type { AccountAlert } from '@/services/accountAlerts.service';

interface AlertHeroCardProps {
  alert: AccountAlert;
  /** "Posted Jun 11" style meta line, rendered above the title. */
  dateLabel?: string | null;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
  /** True once the viewer closed it — the dismiss control becomes a restore. */
  dismissed?: boolean;
  /** Up/down selector shown when more than one announcement is live. */
  pager?: { index: number; total: number; onPrev: () => void; onNext: () => void };
  /** Opens the detail modal (video / more details). */
  onOpenDetail?: () => void;
  /** Status pill for the announcements archive, drawn in the card's own colors. */
  statusLabel?: string;
}

/**
 * The dashboard announcement, rendered as a promo card: background colour,
 * gradient or photo, the copy, and an optional call-to-action button — all
 * authored in the corporate dashboard. Appearance comes from
 * resolveAlertHeroStyle so this matches the builder preview exactly.
 */
export function AlertHeroCard({
  alert,
  dateLabel,
  onDismiss,
  dismissDisabled = false,
  dismissed = false,
  pager,
  onOpenDetail,
  statusLabel,
}: AlertHeroCardProps) {
  const style = resolveAlertHeroStyle(alert);

  // The button falls back to the announcement URL so an author can label a
  // button without re-typing the link they already entered.
  const buttonHref = style.button ? style.button.url || alert.alertURL : '';

  // A photo only shows beside the copy when it is not already the background.
  const sidePhoto = alert.alertPhoto && !alert.bgImage ? alert.alertPhoto : '';

  // Only offer "More Details" when it holds something the card is not showing.
  const showDetails = hasExtraDetails(alert.alertContent, alert.moreDetails);

  // A photo background is drawn under the resolver's own scrim, which holds full
  // strength for bgOverlayCoverage percent of the gradient line - 25 by default -
  // and fades to nothing after that. On a wide card the copy sits inside that
  // quarter. Stacked into one narrow column, the body copy and the call to action
  // run well past it, onto the photo itself. This second wash exists only below lg
  // and is resolved through safeImageUrl, the helper the resolver gates on, so a
  // rejected URL cannot lay a wash over a card that is showing no photo at all.
  const heroPhoto = safeImageUrl(alert.bgImage);
  const mobileScrim = style.isLightText
    ? 'linear-gradient(to bottom, rgba(15, 23, 42, 0) 35%, rgba(15, 23, 42, 0.72) 100%)'
    : 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 35%, rgba(255, 255, 255, 0.78) 100%)';

  // Auto keeps the historical placement: under the photo when there is one,
  // pinned to the card's bottom edge otherwise.
  const buttonPos =
    style.button && style.button.position !== 'auto'
      ? style.button.position
      : sidePhoto
        ? 'top'
        : 'bottom';
  const isMiddle = buttonPos === 'middle';

  // touch-target-h is a min-height declared inside the coarse-pointer block at the
  // bottom of index.css, so the px-/py- utilities above still describe the resting
  // size and only the floor moves. No touch-target-inset here: its margin shorthand
  // sets margin-top too and would beat the mt-auto this button can carry (SCRUM-1480).
  // "Middle" means the middle of the card. Centring it in the flow would only
  // centre it in whatever the column has left after the dismiss chrome and the
  // photo, so it is positioned against the padded row instead - right-7 matches
  // that row's own padding, keeping it on the photo's edge. Out of flow it sizes
  // nothing, though, and the copy column is flex-1: on its own that lets the body
  // copy run the full width of the card and on underneath the button. The spacer
  // rendered below is what holds the column open in its place.
  // Both are lg-only. Below that the card is a single stacked column, where the
  // middle of the card is the middle of the copy - so a middle button joins the
  // stack like the other three positions rather than floating over the text.
  const buttonClass = `inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 touch-target-h ${
    isMiddle
      ? `${sidePhoto ? 'w-full lg:w-44' : 'max-w-full'} lg:absolute lg:right-7 lg:top-1/2 lg:-translate-y-1/2`
      : `${sidePhoto ? 'w-full' : 'max-w-full'} ${buttonPos === 'bottom' ? 'mt-auto' : ''}`
  }`;

  return (
    <div className="relative overflow-hidden rounded-xl shadow-sm" style={style.container}>
      {heroPhoto && (
        /* No z-index: this is positioned and the row below is too, so among
           positioned siblings the later one in DOM order paints on top. */
        <div
          aria-hidden="true"
          className="hidden max-lg:block max-lg:absolute max-lg:inset-0 max-lg:pointer-events-none"
          style={{ backgroundImage: mobileScrim }}
        />
      )}

      {/* The floor is on this row, not the card, so the stretched right column
          (and its bottom-positioned button) reaches the full height.

          Below lg the three columns stack. The nav rail is back in flow at 16rem from
          md, so even at 768 this row leaves the copy only about 238px beside a button
          and a close control, and at 375 about 100px. The layout gate is a width gate
          because every pointer type wants the stacked card; the tap-size gate on the
          controls below is the coarse-pointer band instead, because that question is
          whether a finger is driving it, not how narrow the window is.

          p-7 moved from the sm breakpoint to lg on purpose: Tailwind emits every
          min-width variant AFTER the max-width ones, so an sm: token outranks these
          stacked values through the 640-767 band and the padding would jump back. */}
      <div className="relative flex min-h-[220px] items-start gap-6 p-6 lg:p-7 max-lg:min-h-0 max-lg:flex-col max-lg:items-stretch max-lg:gap-4 max-lg:p-5 max-md:p-4">
        {/* flex-none once stacked: flex-1 sets a zero flex-basis on the MAIN axis,
            which is the height in a column container, not the width it means in a row. */}
        <div className="min-w-0 flex-1 max-lg:flex-none max-lg:pr-11">
          {dateLabel && (
            <p
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: style.mutedTextColor }}
            >
              {dateLabel}
            </p>
          )}

          <h3
            className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight max-lg:break-words"
            style={style.title}
          >
            {/* Inline markup (<b>/<i>/<u>/colour spans) authored in the
                corporate dashboard's title editor, sanitized on write. */}
            {alert.alertURL ? (
              <a
                href={alert.alertURL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                <span dangerouslySetInnerHTML={{ __html: alert.alertTitle }} />
                <ExternalLink className="inline h-3.5 w-3.5 ml-1.5 opacity-60" />
              </a>
            ) : (
              <span dangerouslySetInnerHTML={{ __html: alert.alertTitle }} />
            )}
          </h3>

          {/* Deliberately not `prose`: prose sets its own colours on child
              elements, which would override the authored text colour. */}
          {hasRichTextContent(alert.alertContent) && (
            <div
              className="mt-2 text-base [&_p]:my-1 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold max-lg:break-words"
              style={{ color: style.mutedTextColor }}
              dangerouslySetInnerHTML={{ __html: alert.alertContent }}
            />
          )}

          {(alert.videoURL || showDetails) && (
            <div className="mt-4 flex items-center gap-4">
              {alert.videoURL && (
                <button
                  type="button"
                  onClick={onOpenDetail}
                  className="flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline touch-target-h"
                  style={{ color: style.mutedTextColor }}
                >
                  <PlayCircle className="h-4 w-4" />
                  Watch video
                </button>
              )}
              {showDetails && (
                <button
                  type="button"
                  onClick={onOpenDetail}
                  className="flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline touch-target-h"
                  style={{ color: style.mutedTextColor }}
                >
                  More Details
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right column: dismiss/status chrome on top, then the photo and the
            call to action beneath it. One column rather than two, so its right
            edge lands on the card's padding and the media is inset from the
            edge by the same amount as the copy on the left. */}
        {(sidePhoto || style.button || onDismiss || statusLabel) && (
          <div
            className={`flex shrink-0 flex-col items-end gap-3 self-stretch max-lg:items-stretch ${
              sidePhoto ? 'lg:w-44' : ''
            }`}
          >
            {(onDismiss || statusLabel) && (
              /* Below lg the row stacks, so in flow this would land between the
                 copy and the photo rather than in the corner. Out of flow into
                 the card corner instead - the row is the positioning context,
                 and the copy column reserves pr-11 for it. */
              <div className="flex flex-col items-end gap-2 max-lg:absolute max-lg:right-3 max-lg:top-3">
                {statusLabel && (
                  <span
                    className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: style.mutedTextColor, borderColor: style.borderColor }}
                  >
                    {statusLabel}
                  </span>
                )}

                {onDismiss && (
                  <button
                    type="button"
                    onClick={onDismiss}
                    disabled={dismissDisabled}
                    className="rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-40 touch-target-44 tap-feedback"
                    style={{ color: style.textColor }}
                    aria-label={dismissed ? 'Restore announcement' : 'Close announcement'}
                    title={dismissed ? 'Restore this announcement' : 'Close this announcement'}
                  >
                    {dismissed ? (
                      <RotateCcw className="h-4 w-4 touch-target-icon" />
                    ) : (
                      <X className="h-4 w-4 touch-target-icon" />
                    )}
                  </button>
                )}
              </div>
            )}

            {sidePhoto &&
              (alert.alertURL ? (
                <a href={alert.alertURL} target="_blank" rel="noopener noreferrer">
                  <img
                    src={sidePhoto}
                    alt=""
                    className="h-28 w-44 rounded-lg object-cover transition-opacity hover:opacity-90 max-lg:h-32 max-lg:w-full"
                  />
                </a>
              ) : (
                <img
                  src={sidePhoto}
                  alt=""
                  className="h-28 w-44 rounded-lg object-cover max-lg:h-32 max-lg:w-full"
                />
              ))}

            {/* The middle button's stand-in: the same box with the same label, left
                in flow with visibility:hidden, so the column is still as wide as the
                button that is no longer in it. A side photo already pins the column
                at lg:w-44, so it is only needed without one. */}
            {isMiddle && style.button && !sidePhoto && (
              <span
                aria-hidden="true"
                className="invisible hidden max-w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold lg:inline-flex"
              >
                {style.button.text}
              </span>
            )}

            {style.button &&
              (buttonHref ? (
                <a
                  href={buttonHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonClass}
                  style={style.button.style}
                >
                  {style.button.text}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={onOpenDetail}
                  className={buttonClass}
                  style={style.button.style}
                >
                  {style.button.text}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Bottom center: announcement pager */}
      {pager && pager.total > 1 && (
        /* In flow below lg. Absolute, it reserves no space, so a full-width call to
           action lands on top of it and a 44px chevron would reach up into the copy.
           left-1/2 and bottom-1.5 go inert once static; the transform has to be
           zeroed explicitly because it applies to a static box too. */
        <div className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 items-center gap-1 max-lg:static max-lg:translate-x-0 max-lg:justify-center max-lg:gap-2 max-lg:pb-3">
          <button
            type="button"
            onClick={pager.onPrev}
            disabled={pager.index === 0}
            className="rounded p-0.5 opacity-80 hover:opacity-100 disabled:opacity-30 touch-target-44 tap-feedback"
            style={{ color: style.textColor }}
            aria-label="Previous announcement"
          >
            <ChevronLeft className="h-4 w-4 touch-target-icon" />
          </button>
          <span
            className="text-[11px] font-medium tabular-nums"
            style={{ color: style.mutedTextColor }}
          >
            {pager.index + 1}/{pager.total}
          </span>
          <button
            type="button"
            onClick={pager.onNext}
            disabled={pager.index >= pager.total - 1}
            className="rounded p-0.5 opacity-80 hover:opacity-100 disabled:opacity-30 touch-target-44 tap-feedback"
            style={{ color: style.textColor }}
            aria-label="Next announcement"
          >
            <ChevronRight className="h-4 w-4 touch-target-icon" />
          </button>
        </div>
      )}
    </div>
  );
}
